/* eslint-disable no-param-reassign */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  formatDate,
  getNewDate,
  isEmpty,
} from '../../shared/helper-utils/appUtils';
import { getUserAgentId } from '../../shared/helper-utils/localStorage';
import { validateRescheduleFlightPriceApi } from '../../api/flightApi';
import {
  DATE_MONTH_DAY_FORMAT,
  NAVIGATION_ROUTES,
} from '../../shared/constants/Constants';
import AppAlert from '../../shared/components/app-alert/AppAlert';
import AppLoading from '../../shared/components/app-loading/AppLoading';
import NoSearchResults from '../one-way/NoSearchResults';
import RoundTripIntlResultCard from '../roundt-trip-intl/RoundTripIntlResultCard';

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

const getFlightOptions = (journey) =>
  asArray(journey?.flightoptions).filter(Boolean);

const decorateFlight = (flight) => {
  if (!flight) return undefined;
  const legs = asArray(flight.flightlegs);
  const customString = flight.nextracustomstr || '';
  const fares = asArray(flight.flightfare).map((fare) => {
    const fareData = { ...fare };
    const carriers = [];
    const seats = [];
    const bagWeights = [];
    const bagUnits = [];
    const bookingClasses = [];
    const airFreeText = [];

    legs.forEach((leg) => {
      carriers.push(leg.carrier);
      seats.push(leg.numseatsavailable);
      bagWeights.push(leg.bagweight);
      bagUnits.push(leg.bagunit);
      bookingClasses.push(leg.bookingclass);
      if (leg.weather_origin) airFreeText.push(leg.weather_origin);
    });

    let supplierName = flight.supplier;
    if (isEmpty(supplierName) && customString.includes('AMADEUS')) {
      supplierName = 'amadeus';
    }

    return {
      ...fareData,
      supplierName,
      nextraflightkey: flight.nextraflightkey,
      nextracustomstr: customString,
      flightCarrier: carriers,
      numseatsavailable: seats,
      flightBagWeight: bagWeights,
      flightBagUnit: bagUnits,
      bookingClass: bookingClasses,
      airFreeText,
    };
  });

  return { ...flight, flightlegs: legs, flightfare: fares };
};

const pairFares = (onwardFare, returnFare, totalFare) => ({
  ...onwardFare,
  totalfq: totalFare,
  returnNextraflightkey: returnFare.nextraflightkey,
  returnFare,
  returnFareType: returnFare.faretype,
  returnInclusions: returnFare.inclusions,
  flightCarrierReturn: returnFare.flightCarrier,
  returnNumseatsavailable: returnFare.numseatsavailable,
  flightBagWeightReturn: returnFare.flightBagWeight,
  flightBagUnitReturn: returnFare.flightBagUnit,
  returnBookingClass: returnFare.bookingClass,
  returnCabinBagweight: returnFare.cabinBagweight,
  returnCabinBagunit: returnFare.cabinBagunit,
});

const buildRoundTripResults = (searchResponse) => {
  const journeys = asArray(searchResponse?.flightjourneys);
  if (journeys.length === 0) return [];

  // Some providers return complete round trips in one journey; the reschedule
  // endpoint used by the existing page can also return one journey per leg.
  const firstJourneyOptions = getFlightOptions(journeys[0]);
  const hasCombinedResults = firstJourneyOptions.some(
    (option) => option?.recommendedflight?.[1],
  );

  if (hasCombinedResults) {
    return firstJourneyOptions.flatMap((option) => {
      const onward = decorateFlight(option.recommendedflight[0]);
      const inbound = decorateFlight(option.recommendedflight[1]);
      if (!onward || !inbound) return [];
      const returnFare = inbound.flightfare[0];
      if (!returnFare) return [];
      const combinedFares = onward.flightfare.map((onwardFare) =>
        pairFares(onwardFare, returnFare, onwardFare.totalfq),
      );
      return [
        {
          onward,
          inbound,
          fares: combinedFares.sort((a, b) => a.totalfq - b.totalfq),
          key: `${onward.nextraflightkey}__${inbound.nextraflightkey}`,
        },
      ];
    });
  }

  const returnOptions = getFlightOptions(journeys[1]);
  const outwardFlights = firstJourneyOptions
    .map((option) => decorateFlight(option?.recommendedflight?.[0]))
    .filter((flight) => flight?.flightfare?.length > 0);
  const inboundFlights = returnOptions
    .map((option) => decorateFlight(option?.recommendedflight?.[0]))
    .filter((flight) => flight?.flightfare?.length > 0);

  return outwardFlights.flatMap((onward) =>
    inboundFlights.map((inbound) => {
      const fares = onward.flightfare
        .flatMap((onwardFare) =>
          inbound.flightfare.map((returnFare) =>
            pairFares(
              onwardFare,
              returnFare,
              Number(onwardFare.totalfq || 0) + Number(returnFare.totalfq || 0),
            ),
          ),
        )
        .sort((a, b) => a.totalfq - b.totalfq);
      return {
        onward,
        inbound,
        fares,
        key: `${onward.nextraflightkey}__${inbound.nextraflightkey}`,
      };
    }),
  );
};

export default function RoundtripIntlRescheduleSearchResultsPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { transactionId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [bookingError, setBookingError] = useState(undefined);
  const [selectedFare, setSelectedFare] = useState(undefined);
  const fareByKeyRef = useRef(new Map());
  const selectedFareRef = useRef(undefined);

  const searchRequest = useMemo(() => {
    const request = state?.searchRequest;
    if (!request) return undefined;
    return {
      ...request,
      journeytype: request.journeytype || request.jtype || 'roundtrip',
      onwarddate: request.onwarddate || request.jdate || '',
      returndate: request.returndate || request.rdate || '',
      jdate: request.jdate || request.onwarddate || '',
      rdate: request.rdate || request.returndate || '',
      originLocation: request.originLocation || request.origin || '',
      destinationLocation:
        request.destinationLocation || request.destination || '',
    };
  }, [state?.searchRequest]);

  const searchResults = useMemo(
    () => buildRoundTripResults(state?.searchResponse),
    [state?.searchResponse],
  );

  useEffect(() => {
    const fareMap = new Map();
    searchResults.forEach(({ fares }) => {
      fares.forEach((fare) => {
        if (!fareMap.has(fare.nextraflightkey)) {
          fareMap.set(fare.nextraflightkey, fare);
        }
      });
    });
    fareByKeyRef.current = fareMap;
  }, [searchResults]);

  const handleReprice = async (fare) => {
    if (!fare?.nextraflightkey || !fare?.returnNextraflightkey) {
      setBookingError({
        title: 'Unable to continue',
        description: 'Select a valid onward and return flight.',
      });
      return;
    }

    setSelectedFare(fare);
    selectedFareRef.current = fare;
    setBookingError(undefined);
    setIsLoading(true);
    try {
      const response = await validateRescheduleFlightPriceApi(
        getUserAgentId(),
        transactionId,
        searchRequest?.jtype || searchRequest?.journeytype || 'roundtrip',
        fare.nextraflightkey,
        fare.returnNextraflightkey,
      );
      const repricing = response?.NextraPricingResponseV4;
      if (repricing?.statuscode === '200') {
        const route =
          NAVIGATION_ROUTES.CHANGE_BOOKING.RESCHEDULE_BOOKING.replace(
            ':transactionId',
            transactionId,
          );
        navigate(route, {
          state: {
            repriseResponse: repricing,
            journeyType: 'roundtrip',
            changeBookingSector: state.changeBookingSector,
            airlinePNR: state.airlinePNR,
          },
        });
      } else {
        setBookingError({
          title: 'Failed to reprice flight',
          description:
            response?.ApiStatus?.Message || 'Pricing failed. Please try again.',
        });
      }
    } catch {
      setBookingError({
        title: 'Failed to reprice flight',
        description: 'Pricing failed. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFareSelection = (...selection) => handleReprice(selection[2]);

  const handleNavigateToBookingPage = (fareKey) => {
    const currentFare = selectedFareRef.current;
    handleReprice(
      currentFare?.nextraflightkey === fareKey
        ? currentFare
        : fareByKeyRef.current.get(fareKey),
    );
  };

  return (
    <section className="reschedule-booking-results-page">
      {bookingError && (
        <AppAlert
          variant="error"
          showIcon={false}
          title={bookingError.title}
          description={bookingError.description}
          onHide={() => setBookingError(undefined)}
        />
      )}
      <AppLoading isLoading={isLoading} />
      {searchRequest && (
        <section className="p-3 mb-5 app-container">
          <h3 className="change-booking-header mb-4">
            Change booking for transaction: {state.airlinePNR}
          </h3>
          <div className="reschedule-info search-info d-flex justify-content-between">
            <div className="search-info--item">
              <span className="search-info--item_heading">Journey</span>
              <span className="search-info--item_value">
                {searchRequest.jtype || 'RoundTrip'}
              </span>
            </div>
            <div className="search-info--item">
              <span className="search-info--item_heading">Destination</span>
              <span className="search-info--item_value primary-color">
                {`${searchRequest.origin} - ${searchRequest.destination}`}
              </span>
            </div>
            <div className="search-info--item">
              <span className="search-info--item_heading">Onward date</span>
              <span className="search-info--item_value">
                {formatDate(
                  getNewDate(searchRequest.jdate),
                  DATE_MONTH_DAY_FORMAT,
                )}
              </span>
            </div>
            <div className="search-info--item">
              <span className="search-info--item_heading">Return date</span>
              <span className="search-info--item_value">
                {formatDate(
                  getNewDate(searchRequest.rdate),
                  DATE_MONTH_DAY_FORMAT,
                )}
              </span>
            </div>
            <div className="search-info--item">
              <span className="search-info--item_heading">Passengers</span>
              <span className="search-info--item_value">
                {parseInt(searchRequest.numadults || 0, 10) +
                  parseInt(searchRequest.numchildren || 0, 10) +
                  parseInt(searchRequest.numinfants || 0, 10)}{' '}
                Passengers
              </span>
            </div>
          </div>
        </section>
      )}
      <div className="app-container">
        {searchRequest &&
          searchResults.map(({ onward, inbound, fares, key }, index) => (
            <RoundTripIntlResultCard
              key={key}
              onwardFlight={{ ...onward, flightfare: fares }}
              returnFlight={inbound}
              onwardFares={fares}
              selectedFare={selectedFare}
              onFareSelection={handleFareSelection}
              onSelectForEmail={() => {}}
              showInvoiceFare={false}
              flightsForEmail={[]}
              searchRequest={searchRequest}
              index={index}
              isAmPmFormat={false}
              conversionRate={1}
              currencyOption="INR"
              onNavigateToBookingPage={handleNavigateToBookingPage}
              onShareViaWhatsapp={() => {}}
              onShareViaEmail={() => {}}
            />
          ))}
        {searchResults.length === 0 && (
          <NoSearchResults message="Please change your filter criteria and try again" />
        )}
      </div>
    </section>
  );
}
